import React, { ComponentProps } from 'react'
import { useRouter } from 'hooks/useRouter'
import useTranslation from 'next-translate/useTranslation'
import { IconName } from 'components/Icon/iconNames'
import { Layout } from 'components/Layout'
import { Header } from 'components/Header'
import { Footer } from 'components/Footer'
import { Text } from 'components/Text'
import { Stack } from 'components/Stack'
import { Tabs } from 'features/User/PetDetail/components/Tabs'
import { TabContent } from './components/TabContent'
import { VerticalSeparator } from 'components/VerticalSeparator'
import { TranslationKeys } from 'locales/translationKeys'
import { FaqsRoot, Wrapper, TitleSection, CardSection, FAQsCard } from './Faqs.styles'

type FaqsOwnProps = ComponentProps<typeof FaqsRoot>
export type FaqsProps = FaqsOwnProps & {}

export type FaqsType = {
  question: TranslationKeys
  answer: TranslationKeys
  link?: string
}

const FOOD_FAQS: FaqsType[] = [
  {
    question: 'faqs:food.balanced.question',
    answer: 'faqs:food.balanced.answer',
  },
  {
    question: 'faqs:food.aafco.question',
    answer: 'faqs:food.aafco.answer',
  },
  {
    question: 'faqs:food.nutritionist.question',
    answer: 'faqs:food.nutritionist.answer',
  },
  {
    question: 'faqs:food.start.question',
    answer: 'faqs:food.start.answer',
  },
  {
    question: 'faqs:food.kibble.question',
    answer: 'faqs:food.kibble.answer',
  },
  {
    question: 'faqs:food.add.question',
    answer: 'faqs:food.add.answer',
  },
  {
    question: 'faqs:food.origin.question',
    answer: 'faqs:food.origin.answer',
  },
  {
    question: 'faqs:food.bone.question',
    answer: 'faqs:food.bone.answer',
  },
  {
    question: 'faqs:food.safety.question',
    answer: 'faqs:food.safety.answer',
  },
  {
    question: 'faqs:food.cook.question',
    answer: 'faqs:food.cook.answer',
  },
  {
    question: 'faqs:food.fresh.question',
    answer: 'faqs:food.fresh.answer',
  },
  {
    question: 'faqs:food.portion.question',
    answer: 'faqs:food.portion.answer',
  },
  {
    question: 'faqs:food.thaw.question',
    answer: 'faqs:food.thaw.answer',
  },
  {
    question: 'faqs:food.cats.question',
    answer: 'faqs:food.cats.answer',
  },
]

const SHIPPING_FAQS: FaqsType[] = [
  {
    question: 'faqs:shipping.nationwide.question',
    answer: 'faqs:shipping.nationwide.answer',
  },
  {
    question: 'faqs:shipping.bulk.question',
    answer: 'faqs:shipping.bulk.answer',
  },
  {
    question: 'faqs:shipping.meal-plans.question',
    answer: 'faqs:shipping.meal-plans.answer',
  },
  {
    question: 'faqs:shipping.arrive.question',
    answer: 'faqs:shipping.arrive.answer',
  },
  {
    question: 'faqs:shipping.new-order.question',
    answer: 'faqs:shipping.new-order.answer',
  },
  {
    question: 'faqs:shipping.coolers.question',
    answer: 'faqs:shipping.coolers.answer',
  },
  {
    question: 'faqs:shipping.delivery.question',
    answer: 'faqs:shipping.delivery.answer',
  },
  {
    question: 'faqs:shipping.cancellation.question',
    answer: 'faqs:shipping.cancellation.answer',
  },
  {
    question: 'faqs:shipping.change.question',
    answer: 'faqs:shipping.change.answer',
  },
  {
    question: 'faqs:shipping.refund.question',
    answer: 'faqs:shipping.refund.answer',
  },
]

const SUBSCRIPTION_FAQS: FaqsType[] = [
  {
    question: 'faqs:subscription.tracking.question',
    answer: 'faqs:subscription.tracking.answer',
  },
  {
    question: 'faqs:subscription.change.question',
    answer: 'faqs:subscription.change.answer',
  },
  {
    question: 'faqs:subscription.pause.question',
    answer: 'faqs:subscription.pause.answer',
  },
  {
    question: 'faqs:subscription.billing.question',
    answer: 'faqs:subscription.billing.answer',
  },
  {
    question: 'faqs:subscription.password.question',
    answer: 'faqs:subscription.password.answer',
    link: '/auth/reset-password',
  },
]

const GENERAL_FAQS: FaqsType[] = [
  {
    question: 'faqs:general.cooked.question',
    answer: 'faqs:general.cooked.answer',
  },
  {
    question: 'faqs:general.teeth.question',
    answer: 'faqs:general.teeth.answer',
  },
  {
    question: 'faqs:general.new.question',
    answer: 'faqs:general.new.answer',
    link: '/blog/my-dog-wont-eat-raw-food',
  },
  {
    question: 'faqs:general.cat.question',
    answer: 'faqs:general.cat.answer',
  },
  {
    question: 'faqs:general.pooping.question',
    answer: 'faqs:general.pooping.answer',
  },
  {
    question: 'faqs:general.stool.question',
    answer: 'faqs:general.stool.answer',
  },
  {
    question: 'faqs:general.detoxification.question',
    answer: 'faqs:general.detoxification.answer',
  },
  {
    question: 'faqs:general.drinking.question',
    answer: 'faqs:general.drinking.answer',
  },
  {
    question: 'faqs:general.blood-thirsty.question',
    answer: 'faqs:general.blood-thirsty.answer',
  },
]
export const Faqs = (_props: FaqsProps) => {
  const { t } = useTranslation()

  const router = useRouter()
  const { query } = useRouter()

  const header = <Header />

  const FAQS_TABS = [
    {
      label: t('faqs:food.title'),
      icon: 'restaurant' as IconName,
      content: <TabContent title={t('faqs:food.title')} faqs={FOOD_FAQS} />,
      href: 'ourFood=true',
    },
    {
      label: t('faqs:shipping.title'),
      icon: 'cube' as IconName,
      content: <TabContent title={t('faqs:shipping.title')} faqs={SHIPPING_FAQS} />,
      href: 'shippingAndPackaging=true',
    },
    {
      label: t('faqs:subscription.title'),
      icon: 'subscription' as IconName,
      content: <TabContent title={t('faqs:subscription.title')} faqs={SUBSCRIPTION_FAQS} />,
      href: 'yourSubscription=true',
    },
    {
      label: t('faqs:general.title'),
      icon: 'comment' as IconName,
      content: <TabContent title={t('faqs:general.title')} faqs={GENERAL_FAQS} />,
      href: 'generalQuestions=true',
    },
  ]

  const defaultTab =
    FAQS_TABS.find((tab) => tab.href.includes(Object.keys(query)[0]))?.label || FAQS_TABS[0]?.label

  const onValueChange = (value: string) => {
    const activeTab = FAQS_TABS.find((tab) => tab.label === value)
    window.history.replaceState({}, '', `?${activeTab?.href}`)
    // router.push(activeTab?.href || router.pathname, undefined, { shallow: true })
  }

  return (
    <FaqsRoot>
      <Layout header={header} hideCTA>
        <Wrapper>
          <Stack direction="column" gap="5" css={{ width: ' $max' }}>
            <TitleSection>
              <Stack alignItems="center" gap="5">
                <Text size="h3" as="h3" weight="bold" color="primary-800">
                  {t('faqs:title')}
                </Text>
                <VerticalSeparator />
                <Text size="headline" color="primary-800">
                  {t('faqs:subtitle')}
                </Text>
              </Stack>
            </TitleSection>
            <CardSection>
              <FAQsCard>
                <Tabs tabs={FAQS_TABS} defaultTab={defaultTab} onValueChange={onValueChange} />
              </FAQsCard>
            </CardSection>
          </Stack>
        </Wrapper>
        <Footer />
      </Layout>
    </FaqsRoot>
  )
}

Faqs.displayName = 'Faqs'
